import { ChevronDownIcon, VolumeIcon } from '@assets/icons';
import { IconButton, TableView } from '@components/button';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { Inpaint } from '@generated/graphql';
import { Box, Divider, Stack, useTheme } from '@mui/material';
import { useObservableState } from 'observable-hooks';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { InpaintPreview } from './InpaintPreview';
import { useInpaintModal } from './VolumeInpaintProvider';

type InpaintProgressModalProps = {
  tableView: TableView;
  workspaceId: string;
  setFolderHistory: Dispatch<SetStateAction<string[]>>;
};

export const InpaintProgressModal = ({
  tableView,
  workspaceId,
  setFolderHistory,
}: InpaintProgressModalProps) => {
  const { palette } = useTheme();
  const { data: inpaints, fetchMore, refetch, open$ } = useInpaintModal();
  const open = useObservableState(open$);

  const handleToggle = () => {
    open$.next(!open);
  };

  const item = useCallback(
    (_index, inpaint: Inpaint) => (
      <InpaintPreview
        workspaceId={workspaceId}
        inpaint={inpaint}
        refetch={refetch}
        setFolderHistory={setFolderHistory}
      />
    ),
    [refetch],
  );

  if (inpaints.length < 1) return null;

  return (
    <Stack
      sx={{
        backgroundColor:
          tableView === 'grid' ? palette.background.paper : palette.background.default,
        width: '500px',
      }}
    >
      <Group
        justifyContent="space-between"
        alignItems="center"
        sx={{
          pl: 3,
          pr: 1,
          py: 2,
          borderTopLeftRadius: '2px',
          borderTopRightRadius: '2px',
          opacity: 0.8,
        }}
      >
        <Group gap={2}>
          <VolumeIcon color={palette.text.paper} />
          <Typography color={palette.text.paper}>
            Create Jobs (
            {
              inpaints.filter((inpaint) =>
                ['queued', 'starting', 'running'].includes(inpaint.status),
              ).length
            }
            )
          </Typography>
        </Group>
        <IconButton onClick={handleToggle} Icon={ChevronDownIcon} size={16} />
      </Group>
      <Divider />
      {open && (
        <Box
          height="200px"
          data-test-id="virtuoso-item-list"
          style={{ overflowY: 'auto', overflowX: 'hidden' }}
        >
          <Virtuoso
            totalCount={inpaints.length}
            overscan={1000}
            endReached={fetchMore}
            data={inpaints}
            itemContent={item}
          />
        </Box>
      )}
    </Stack>
  );
};
